import React, { useEffect, useState, useCallback } from "react";
import { api, brl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Receipt, CurrencyDollar, UsersThree, Package, Trophy, Crown } from "@phosphor-icons/react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5 shadow-none border-border" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</p>
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accent ? "bg-accent/10 text-accent" : "bg-zinc-100 text-zinc-700"}`}>
          <Icon size={20} weight="bold" />
        </div>
      </div>
      <p className="mt-3 font-heading text-3xl font-bold text-zinc-950 tabular-nums">{value}</p>
    </Card>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);

  const loadDashboard = useCallback(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  if (!d) return <div className="text-muted-foreground">Carregando...</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Visão geral da operação DUALT.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Receipt} label="Pedidos no mês" value={d.pedidos_mes} accent />
        <Stat icon={CurrencyDollar} label="Faturado no mês" value={brl(d.total_faturado_mes)} accent />
        <Stat icon={UsersThree} label="Clientes" value={d.total_clientes} />
        <Stat icon={Package} label="Produtos" value={d.total_produtos} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-none border-border">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
            <Trophy size={20} weight="bold" className="text-accent" />
            <h3 className="font-heading font-semibold text-zinc-900">Top Produtos</h3>
          </div>
          <div className="divide-y divide-border">
            {d.top_produtos.length === 0 && <p className="p-5 text-sm text-muted-foreground">Sem dados ainda.</p>}
            {d.top_produtos.map((p, i) => (
              <div key={`${p.nome}-${i}`} className="flex items-center justify-between px-5 py-3">
                <span className="text-sm text-zinc-800 truncate pr-4">{p.nome}</span>
                <span className="text-sm font-semibold tabular-nums text-zinc-900">{p.quantidade}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="shadow-none border-border">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
            <Crown size={20} weight="bold" className="text-accent" />
            <h3 className="font-heading font-semibold text-zinc-900">Top Clientes</h3>
          </div>
          <div className="divide-y divide-border">
            {d.top_clientes.length === 0 && <p className="p-5 text-sm text-muted-foreground">Sem dados ainda.</p>}
            {d.top_clientes.map((c, i) => (
              <div key={`${c.nome}-${i}`} className="flex items-center justify-between px-5 py-3">
                <span className="text-sm text-zinc-800 truncate pr-4">{c.nome}</span>
                <span className="text-sm font-semibold tabular-nums text-zinc-900">{brl(c.total)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
